import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { Trophy, Play, RotateCcw, Flag, Zap, Timer, Gauge, User, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { racerSubmit, racerLeaderboard } from "@/lib/api";
import AdReward from "./_adReward";

/**
 * Jixxeo Racer — a 3D neon top-down racer.
 *
 * Modes:
 *  • Solo (1P): global leaderboard, touch controls on mobile.
 *  • Split-screen 2P: two independent Canvas viewports, WASD vs Arrows.
 */

const TRACK_POINTS = [
  [0, 0, -30], [22, 0, -28], [34, 0, -14], [30, 0, 6],
  [16, 0, 18], [0, 0, 26], [-16, 0, 22], [-30, 0, 8],
  [-34, 0, -10], [-24, 0, -26], [-8, 0, -32],
];
const TRACK_WIDTH = 6.5;

function makeTrack() {
  const pts = TRACK_POINTS.map((p) => new THREE.Vector3(p[0], p[1], p[2]));
  return new THREE.CatmullRomCurve3(pts, true, "catmullrom", 0.5);
}

function buildWalls(curve, count = 240) {
  const points = curve.getSpacedPoints(count);
  const inner = [], outer = [];
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const next = points[(i + 1) % points.length];
    const tangent = new THREE.Vector3().subVectors(next, p).normalize();
    const normal = new THREE.Vector3(-tangent.z, 0, tangent.x);
    inner.push(p.clone().addScaledVector(normal, -TRACK_WIDTH));
    outer.push(p.clone().addScaledVector(normal, TRACK_WIDTH));
  }
  return { inner, outer, sampled: points };
}

function distToTrack(px, pz, sampled) {
  let best = Infinity;
  for (let i = 0; i < sampled.length; i++) {
    const dx = sampled[i].x - px, dz = sampled[i].z - pz;
    const d = dx * dx + dz * dz;
    if (d < best) best = d;
  }
  return Math.sqrt(best);
}

function TrackVisual({ curve }) {
  const { inner, outer, sampled } = useMemo(() => buildWalls(curve, 260), [curve]);

  const roadGeo = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    const verts = [], idx = [];
    for (let i = 0; i < sampled.length; i++) {
      verts.push(inner[i].x, 0.02, inner[i].z);
      verts.push(outer[i].x, 0.02, outer[i].z);
    }
    for (let i = 0; i < sampled.length; i++) {
      const a = i * 2, b = i * 2 + 1;
      const c = ((i + 1) % sampled.length) * 2;
      const d = ((i + 1) % sampled.length) * 2 + 1;
      idx.push(a, b, c, b, d, c);
    }
    geo.setAttribute("position", new THREE.Float32BufferAttribute(verts, 3));
    geo.setIndex(idx);
    geo.computeVertexNormals();
    return geo;
  }, [inner, outer, sampled]);

  const wallGeo = (pts, height = 0.9) => {
    const geo = new THREE.BufferGeometry();
    const verts = [], idx = [];
    for (let i = 0; i < pts.length; i++) {
      verts.push(pts[i].x, 0.05, pts[i].z);
      verts.push(pts[i].x, 0.05 + height, pts[i].z);
    }
    for (let i = 0; i < pts.length; i++) {
      const a = i * 2, b = i * 2 + 1;
      const c = ((i + 1) % pts.length) * 2;
      const d = ((i + 1) % pts.length) * 2 + 1;
      idx.push(a, c, b, b, c, d);
    }
    geo.setAttribute("position", new THREE.Float32BufferAttribute(verts, 3));
    geo.setIndex(idx);
    geo.computeVertexNormals();
    return geo;
  };

  const innerWallGeo = useMemo(() => wallGeo(inner, 0.9), [inner]);
  const outerWallGeo = useMemo(() => wallGeo(outer, 0.9), [outer]);

  const startLine = useMemo(() => {
    const p = sampled[0];
    const next = sampled[1];
    const tangent = new THREE.Vector3().subVectors(next, p).normalize();
    const normal = new THREE.Vector3(-tangent.z, 0, tangent.x);
    const a = p.clone().addScaledVector(normal, -TRACK_WIDTH + 0.3);
    const b = p.clone().addScaledVector(normal, TRACK_WIDTH - 0.3);
    return { a, b };
  }, [sampled]);

  const stripes = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 8; i++) {
      const t = (i + 0.5) / 8;
      arr.push(new THREE.Vector3().lerpVectors(startLine.a, startLine.b, t));
    }
    return arr;
  }, [startLine]);

  return (
    <group>
      <mesh position={[0, -0.02, 0]} rotation-x={-Math.PI / 2} receiveShadow>
        <planeGeometry args={[160, 160]} />
        <meshStandardMaterial color="#050510" />
      </mesh>
      <gridHelper args={[160, 40, "#00F0FF", "#08151a"]} position={[0, 0, 0]} />
      <mesh geometry={roadGeo} receiveShadow>
        <meshStandardMaterial color="#151520" metalness={0.2} roughness={0.85} />
      </mesh>
      <mesh geometry={innerWallGeo}>
        <meshStandardMaterial color="#00F0FF" emissive="#00F0FF" emissiveIntensity={2.4} side={THREE.DoubleSide} transparent opacity={0.85} />
      </mesh>
      <mesh geometry={outerWallGeo}>
        <meshStandardMaterial color="#FF003C" emissive="#FF003C" emissiveIntensity={2.4} side={THREE.DoubleSide} transparent opacity={0.85} />
      </mesh>
      {sampled.map((p, i) => (i % 6 === 0) && (
        <mesh key={i} position={[p.x, 0.03, p.z]} rotation-x={-Math.PI / 2}>
          <planeGeometry args={[0.35, 1.2]} />
          <meshBasicMaterial color="#FFE300" transparent opacity={0.8} />
        </mesh>
      ))}
      {stripes.map((p, i) => (
        <mesh key={`s${i}`} position={[p.x, 0.04, p.z]} rotation-x={-Math.PI / 2}>
          <planeGeometry args={[TRACK_WIDTH * 2 / 8, 1.4]} />
          <meshBasicMaterial color={i % 2 === 0 ? "#ffffff" : "#050505"} />
        </mesh>
      ))}
    </group>
  );
}

function Car({ carRef, headingRef, color = "#00F0FF" }) {
  const meshRef = useRef();
  useFrame(() => {
    if (!meshRef.current) return;
    meshRef.current.position.set(carRef.current.x, 0.35, carRef.current.z);
    meshRef.current.rotation.y = headingRef.current;
  });
  return (
    <group ref={meshRef}>
      <mesh castShadow>
        <boxGeometry args={[1.4, 0.5, 2.4]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.7} metalness={0.5} roughness={0.25} />
      </mesh>
      <mesh position={[0, 0.35, -0.1]}>
        <boxGeometry args={[0.9, 0.25, 1.1]} />
        <meshStandardMaterial color="#050510" metalness={0.6} roughness={0.2} />
      </mesh>
      <mesh position={[0, 0.05, 1.1]}>
        <boxGeometry args={[1.4, 0.15, 0.05]} />
        <meshBasicMaterial color="#FF003C" />
      </mesh>
      <mesh position={[0, 0.05, -1.15]}>
        <boxGeometry args={[1.35, 0.15, 0.05]} />
        <meshBasicMaterial color="#FFE300" />
      </mesh>
    </group>
  );
}

function TopDownCamera({ carRef, headingRef }) {
  useFrame(({ camera }) => {
    const car = carRef.current;
    const back = 8, height = 12;
    const cx = car.x - Math.sin(headingRef.current) * back;
    const cz = car.z - Math.cos(headingRef.current) * back;
    camera.position.set(cx, height, cz);
    camera.lookAt(car.x, 0.5, car.z);
  });
  return null;
}

/**
 * Physics + input for one player. `keys` is a ref object with a truthy value
 * for each held control: forward/back/left/right OR analog `steer` (-1..1)
 * and `throttle` (0..1) from the touch HUD.
 */
function PhysicsPlayer({ carRef, headingRef, speedRef, keysRef, curve, walls, onOffTrack, onLapCross }) {
  const lastSideRef = useRef(null);
  useFrame((_, dt) => {
    const car = carRef.current;
    const keys = keysRef.current;
    // Discrete keyboard input
    const kbForward = (keys.forward ? 1 : 0);
    const kbBack = (keys.back ? 1 : 0);
    const kbLeft = (keys.left ? 1 : 0);
    const kbRight = (keys.right ? 1 : 0);
    // Analog input (touch pad + paddles) — fall back to keyboard
    const throttle = typeof keys.throttle === "number" ? keys.throttle : kbForward;
    const brake = typeof keys.brake === "number" ? keys.brake : kbBack;
    const steer = typeof keys.steer === "number" ? keys.steer : (kbLeft - kbRight);

    const MAX_SPEED = 22, ACCEL = 14, DECEL = 8, REVERSE_MAX = -10;
    let s = speedRef.current;
    if (throttle > 0.02) s += ACCEL * throttle * dt;
    else if (brake > 0.02) s -= ACCEL * brake * dt;
    else {
      const f = DECEL * dt;
      s = s > 0 ? Math.max(0, s - f) : Math.min(0, s + f);
    }
    s = Math.max(REVERSE_MAX, Math.min(MAX_SPEED, s));
    speedRef.current = s;

    const STEER = 2.2;
    const speedScale = Math.min(1, Math.abs(s) / 4);
    headingRef.current += steer * STEER * dt * speedScale * (s >= 0 ? 1 : -1);

    car.x += Math.sin(headingRef.current) * s * dt;
    car.z += Math.cos(headingRef.current) * s * dt;

    const d = distToTrack(car.x, car.z, walls.sampled);
    const on = d < TRACK_WIDTH - 0.4;
    if (!on) {
      speedRef.current *= (1 - 0.03);
      onOffTrack && onOffTrack(true);
    } else {
      onOffTrack && onOffTrack(false);
    }

    const p = curve.getPointAt(0);
    const nxt = curve.getPointAt(0.005);
    const tangent = new THREE.Vector3().subVectors(nxt, p).normalize();
    const normal = new THREE.Vector3(-tangent.z, 0, tangent.x);
    const dx = car.x - p.x, dz = car.z - p.z;
    const dot = dx * tangent.x + dz * tangent.z;
    const across = dx * normal.x + dz * normal.z;
    const within = Math.abs(across) < TRACK_WIDTH + 0.5;
    const side = dot > 0 ? "fwd" : "back";
    if (lastSideRef.current && lastSideRef.current !== side && within && side === "fwd") {
      onLapCross && onLapCross();
    }
    lastSideRef.current = side;
  });
  return null;
}

/**
 * A single self-contained race view. Owns its car, physics, camera and HUD.
 */
function RacerView({
  color, playerLabel, keysRef, curve, walls,
  onLapDone, onFinish, totalLaps, canvasAspect = "16 / 9", showTouchHud = false,
}) {
  const startPoint = useMemo(() => curve.getPointAt(0.01), [curve]);
  const carRef = useRef({ x: startPoint.x, z: startPoint.z });
  const headingRef = useRef(0);
  const speedRef = useRef(0);

  const [lap, setLap] = useState(0);
  const [lapTimes, setLapTimes] = useState([]);
  const [currentLapMs, setCurrentLapMs] = useState(0);
  const [offTrack, setOffTrack] = useState(false);
  const [speedDisplay, setSpeedDisplay] = useState(0);

  const lapStartTsRef = useRef(0);
  const finishedRef = useRef(false);

  useEffect(() => {
    // Snap car to start line facing the curve direction
    const p = curve.getPointAt(0.01);
    const nxt = curve.getPointAt(0.02);
    carRef.current = { x: p.x, z: p.z };
    headingRef.current = Math.atan2(nxt.x - p.x, nxt.z - p.z);
    speedRef.current = 0;
  }, [curve]);

  useEffect(() => {
    const id = setInterval(() => {
      if (lapStartTsRef.current) setCurrentLapMs(performance.now() - lapStartTsRef.current);
      setSpeedDisplay(Math.round(Math.abs(speedRef.current) * 8));
    }, 60);
    return () => clearInterval(id);
  }, []);

  const handleLapCross = () => {
    if (finishedRef.current) return;
    if (!lapStartTsRef.current) {
      lapStartTsRef.current = performance.now();
      setLap(1);
      return;
    }
    const ms = performance.now() - lapStartTsRef.current;
    lapStartTsRef.current = performance.now();
    setLapTimes((prev) => {
      const next = [...prev, ms];
      onLapDone && onLapDone(ms, next);
      if (next.length >= totalLaps) {
        finishedRef.current = true;
        onFinish && onFinish(next);
      } else {
        setLap(next.length + 1);
      }
      return next;
    });
  };

  const bestLap = lapTimes.length ? Math.min(...lapTimes) : 0;

  return (
    <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-black" style={{ aspectRatio: canvasAspect }}>
      <Canvas
        camera={{ position: [0, 20, 12], fov: 50 }}
        gl={{ antialias: true, powerPreference: "high-performance" }}
        shadows
      >
        <Suspense fallback={null}>
          <color attach="background" args={["#050510"]} />
          <fog attach="fog" args={["#050510", 20, 90]} />
          <ambientLight intensity={0.45} />
          <directionalLight position={[10, 20, 5]} intensity={0.6} castShadow />
          <pointLight position={[0, 15, 0]} intensity={1.4} color="#00F0FF" distance={70} />
          <TrackVisual curve={curve} />
          <Car carRef={carRef} headingRef={headingRef} color={color} />
          <TopDownCamera carRef={carRef} headingRef={headingRef} />
          <PhysicsPlayer
            carRef={carRef} headingRef={headingRef} speedRef={speedRef}
            keysRef={keysRef} curve={curve} walls={walls}
            onOffTrack={setOffTrack} onLapCross={handleLapCross}
          />
        </Suspense>
      </Canvas>

      {/* Header ribbon */}
      <div className="absolute top-2 left-2 right-2 flex items-center gap-2 pointer-events-none">
        <div className="mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border font-bold"
          style={{ borderColor: `${color}55`, background: `${color}22`, color }}>
          {playerLabel}
        </div>
        <div className="mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-white/15 text-white/70">
          Lap {lap || 0}/{totalLaps}
        </div>
        <div className="mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-white/15 text-white/70">
          Speed {speedDisplay}
        </div>
        <div className="ml-auto mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-white/15 text-white/70">
          Best {bestLap ? formatMs(bestLap) : "—"}
        </div>
      </div>

      {/* Current lap big timer */}
      {lap > 0 && !finishedRef.current && (
        <div className="absolute top-12 left-3 pointer-events-none">
          <div className="mono text-[10px] uppercase tracking-widest text-white/50">// lap {lap}</div>
          <div className="font-heading font-black text-3xl tabular-nums" style={{ color, textShadow: `0 0 12px ${color}` }}>
            {formatMs(currentLapMs)}
          </div>
        </div>
      )}
      {offTrack && (
        <div className="absolute top-12 right-3 mono text-[10px] uppercase tracking-widest text-[#FF003C] animate-pulse pointer-events-none">
          ⚠ Off track
        </div>
      )}

      {/* Touch HUD — solo mode only */}
      {showTouchHud && !finishedRef.current && (
        <TouchHUD keysRef={keysRef} accent={color} />
      )}
    </div>
  );
}

/**
 * Analog thumb-steering pad + throttle/brake paddles for mobile.
 * Writes `.steer` (-1..1), `.throttle` (0..1) and `.brake` (0..1) into keysRef.
 */
function TouchHUD({ keysRef, accent = "#00F0FF" }) {
  const padRef = useRef(null);
  const [padPos, setPadPos] = useState({ x: 0, y: 0 });
  const [padActive, setPadActive] = useState(false);
  const [gasActive, setGasActive] = useState(false);
  const [brakeActive, setBrakeActive] = useState(false);

  const setSteer = (v) => { keysRef.current.steer = v; };
  const setThrottle = (v) => { keysRef.current.throttle = v; };
  const setBrake = (v) => { keysRef.current.brake = v; };

  const onPadStart = (e) => {
    e.preventDefault();
    setPadActive(true);
    updatePad(e);
  };
  const onPadMove = (e) => {
    if (!padActive) return;
    e.preventDefault();
    updatePad(e);
  };
  const onPadEnd = (e) => {
    if (e) e.preventDefault && e.preventDefault();
    setPadActive(false);
    setPadPos({ x: 0, y: 0 });
    setSteer(0);
  };
  const updatePad = (e) => {
    if (!padRef.current) return;
    const rect = padRef.current.getBoundingClientRect();
    const touch = (e.touches && e.touches[0]) || e;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (touch.clientX - cx);
    const dy = (touch.clientY - cy);
    const r = rect.width / 2;
    const clampX = Math.max(-r, Math.min(r, dx));
    const clampY = Math.max(-r, Math.min(r, dy));
    setPadPos({ x: clampX, y: clampY });
    setSteer(-clampX / r); // left drag = positive steer (turn left)
  };

  useEffect(() => {
    // safety: clear inputs on unmount
    return () => { setSteer(0); setThrottle(0); setBrake(0); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const gasHandlers = {
    onTouchStart: (e) => { e.preventDefault(); setGasActive(true); setThrottle(1); },
    onTouchEnd:   (e) => { e.preventDefault(); setGasActive(false); setThrottle(0); },
    onMouseDown:  () => { setGasActive(true); setThrottle(1); },
    onMouseUp:    () => { setGasActive(false); setThrottle(0); },
    onMouseLeave: () => { setGasActive(false); setThrottle(0); },
  };
  const brakeHandlers = {
    onTouchStart: (e) => { e.preventDefault(); setBrakeActive(true); setBrake(1); },
    onTouchEnd:   (e) => { e.preventDefault(); setBrakeActive(false); setBrake(0); },
    onMouseDown:  () => { setBrakeActive(true); setBrake(1); },
    onMouseUp:    () => { setBrakeActive(false); setBrake(0); },
    onMouseLeave: () => { setBrakeActive(false); setBrake(0); },
  };

  return (
    <div className="absolute inset-x-0 bottom-0 h-40 pointer-events-none select-none">
      {/* Steering pad (left) */}
      <div
        ref={padRef}
        onTouchStart={onPadStart}
        onTouchMove={onPadMove}
        onTouchEnd={onPadEnd}
        onMouseDown={onPadStart}
        onMouseMove={onPadMove}
        onMouseUp={onPadEnd}
        onMouseLeave={onPadEnd}
        data-testid="racer-touch-steer"
        className="absolute left-3 bottom-3 w-32 h-32 rounded-full border-2 flex items-center justify-center touch-none pointer-events-auto"
        style={{
          borderColor: `${accent}55`,
          background: `radial-gradient(circle at 50% 50%, ${accent}22, ${accent}05 60%, transparent)`,
          boxShadow: padActive ? `0 0 40px ${accent}55` : `0 0 20px ${accent}22`,
        }}
      >
        {/* Knob */}
        <div
          className="w-14 h-14 rounded-full border-2"
          style={{
            transform: `translate(${padPos.x}px, ${padPos.y}px)`,
            background: `${accent}`,
            borderColor: "#050510",
            boxShadow: `0 0 24px ${accent}`,
            transition: padActive ? "none" : "transform 0.2s ease-out",
          }}
        />
        <div className="absolute inset-x-0 -top-6 mono text-[9px] uppercase tracking-widest text-center" style={{ color: accent, opacity: 0.75 }}>
          steer
        </div>
      </div>

      {/* Gas + Brake paddles (right) */}
      <div className="absolute right-3 bottom-3 flex flex-col items-end gap-2 pointer-events-auto">
        <button
          {...gasHandlers}
          data-testid="racer-touch-gas"
          className="w-24 h-16 rounded-2xl border-2 mono text-xs uppercase tracking-widest font-black touch-none select-none flex items-center justify-center gap-1"
          style={{
            borderColor: gasActive ? "#39FF14" : "#39FF1466",
            background: gasActive ? "#39FF14" : "rgba(57,255,20,0.12)",
            color: gasActive ? "#000" : "#39FF14",
            boxShadow: gasActive ? "0 0 30px rgba(57,255,20,0.6)" : "0 0 12px rgba(57,255,20,0.2)",
          }}
        >
          ▲ Gas
        </button>
        <button
          {...brakeHandlers}
          data-testid="racer-touch-brake"
          className="w-24 h-14 rounded-2xl border-2 mono text-xs uppercase tracking-widest font-black touch-none select-none flex items-center justify-center gap-1"
          style={{
            borderColor: brakeActive ? "#FF003C" : "#FF003C66",
            background: brakeActive ? "#FF003C" : "rgba(255,0,60,0.12)",
            color: brakeActive ? "#000" : "#FF003C",
            boxShadow: brakeActive ? "0 0 30px rgba(255,0,60,0.6)" : "0 0 12px rgba(255,0,60,0.2)",
          }}
        >
          ▼ Brake
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Main component
// ============================================================
export default function JixxeoRacer() {
  const { user } = useAuth();
  const curve = useMemo(() => makeTrack(), []);
  const walls = useMemo(() => buildWalls(curve, 200), [curve]);

  const [mode, setMode] = useState(null); // null | "solo" | "coop"
  const [board, setBoard] = useState([]);
  const [isTouch, setIsTouch] = useState(false);

  // Per-player key state (mutable refs live for the duration of a race)
  const p1KeysRef = useRef({ forward: false, back: false, left: false, right: false });
  const p2KeysRef = useRef({ forward: false, back: false, left: false, right: false });

  const [p1Best, setP1Best] = useState(() => Number(localStorage.getItem("jixxeo_racer_best") || 0));
  const [p1Finish, setP1Finish] = useState(null);
  const [p2Finish, setP2Finish] = useState(null);

  const totalLaps = 3;

  useEffect(() => {
    setIsTouch(
      typeof window !== "undefined" &&
      (("ontouchstart" in window) || (navigator.maxTouchPoints || 0) > 0) &&
      window.matchMedia("(pointer: coarse)").matches
    );
    racerLeaderboard(20).then((d) => setBoard(d.leaderboard || [])).catch(() => {});
  }, []);

  // Keyboard: P1 = WASD (both modes). P2 = arrow keys (coop only).
  useEffect(() => {
    if (!mode) return;
    const map = { w: [1, "forward"], s: [1, "back"], a: [1, "left"], d: [1, "right"],
                  arrowup: [2, "forward"], arrowdown: [2, "back"], arrowleft: [2, "left"], arrowright: [2, "right"] };
    const kd = (e) => {
      const k = e.key.toLowerCase();
      const m = map[k];
      if (!m) return;
      if (m[0] === 2 && mode !== "coop") return;
      const ref = m[0] === 1 ? p1KeysRef : p2KeysRef;
      ref.current[m[1]] = true;
      if (["w","a","s","d","arrowup","arrowdown","arrowleft","arrowright"," "].includes(k)) e.preventDefault();
    };
    const ku = (e) => {
      const k = e.key.toLowerCase();
      const m = map[k];
      if (!m) return;
      if (m[0] === 2 && mode !== "coop") return;
      const ref = m[0] === 1 ? p1KeysRef : p2KeysRef;
      ref.current[m[1]] = false;
    };
    window.addEventListener("keydown", kd);
    window.addEventListener("keyup", ku);
    return () => { window.removeEventListener("keydown", kd); window.removeEventListener("keyup", ku); };
  }, [mode]);

  const submitBest = async (bestMs) => {
    if (!user) return;
    try {
      const r = await racerSubmit(Math.round(bestMs), null, totalLaps);
      if (r.improved) {
        toast.success(`New global rank: #${r.rank} 🏁`, { description: `${formatMs(bestMs)} lap saved.` });
        const d = await racerLeaderboard(20);
        setBoard(d.leaderboard || []);
      }
    } catch {}
  };

  const onP1LapDone = (ms, times) => {
    const bestSoFar = Math.min(...times);
    if (!p1Best || bestSoFar < p1Best) {
      setP1Best(bestSoFar);
      localStorage.setItem("jixxeo_racer_best", String(Math.round(bestSoFar)));
    }
  };
  const onP1Finish = (times) => {
    const bestMs = Math.min(...times);
    setP1Finish({ times, best: bestMs, total: times.reduce((a, b) => a + b, 0) });
    if (mode === "solo") submitBest(bestMs);
  };
  const onP2Finish = (times) => {
    const bestMs = Math.min(...times);
    setP2Finish({ times, best: bestMs, total: times.reduce((a, b) => a + b, 0) });
  };

  const start = (m) => {
    // reset inputs
    p1KeysRef.current = { forward: false, back: false, left: false, right: false };
    p2KeysRef.current = { forward: false, back: false, left: false, right: false };
    setP1Finish(null); setP2Finish(null);
    setMode(m);
  };
  const back = () => { setMode(null); setP1Finish(null); setP2Finish(null); };

  const raceComplete = mode === "solo"
    ? !!p1Finish
    : !!(p1Finish && p2Finish);
  const coopWinner = p1Finish && p2Finish
    ? (p1Finish.total <= p2Finish.total ? 1 : 2)
    : null;

  return (
    <div className="min-h-screen bg-[#050510] text-white p-4 md:p-8 relative overflow-hidden flex flex-col">
      <div className="relative max-w-6xl w-full mx-auto flex flex-col">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-10 h-10 rounded-xl bg-[#FFE300] flex items-center justify-center shadow-[0_0_24px_rgba(255,227,0,0.55)]">
            <Flag className="w-5 h-5 text-black" strokeWidth={3} />
          </span>
          <div>
            <div className="mono text-[9px] uppercase tracking-[0.25em] text-[#00F0FF]">// jixxeo apps · demo pwa · webgl</div>
            <h1 className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tighter leading-none">Jixxeo Racer</h1>
          </div>
          {mode && (
            <button onClick={back}
              className="ml-auto inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest border border-white/15 text-white/70 hover:text-white px-3 py-2 rounded-full">
              <RotateCcw className="w-3.5 h-3.5" /> Menu
            </button>
          )}
        </div>

        {!mode && <ModeSelect onStart={start} p1Best={p1Best} isTouch={isTouch} board={board} user={user} />}

        {mode === "solo" && (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
            <RacerView
              color="#00F0FF" playerLabel="You (P1)"
              keysRef={p1KeysRef} curve={curve} walls={walls}
              onLapDone={onP1LapDone} onFinish={onP1Finish}
              totalLaps={totalLaps} canvasAspect="16 / 9"
              showTouchHud={isTouch}
            />
            <Leaderboard board={board} user={user} />
          </div>
        )}

        {mode === "coop" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <RacerView
              color="#00F0FF" playerLabel="P1 · WASD"
              keysRef={p1KeysRef} curve={curve} walls={walls}
              onLapDone={onP1LapDone} onFinish={onP1Finish}
              totalLaps={totalLaps} canvasAspect="4 / 3"
              showTouchHud={false}
            />
            <RacerView
              color="#FF003C" playerLabel="P2 · Arrows"
              keysRef={p2KeysRef} curve={curve} walls={walls}
              onLapDone={() => {}} onFinish={onP2Finish}
              totalLaps={totalLaps} canvasAspect="4 / 3"
              showTouchHud={false}
            />
          </div>
        )}

        {raceComplete && (
          <div className="mt-4 glass rounded-2xl p-6 text-center border border-[#FFE300]/40">
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#FFE300] mb-2">// results</div>
            {mode === "solo" && p1Finish && (
              <>
                <div className="font-heading font-black text-3xl uppercase tracking-tighter">Race complete</div>
                <div className="mono text-sm text-white/70 mt-2">
                  Best lap <span className="text-[#39FF14] font-bold">{formatMs(p1Finish.best)}</span>
                  {" · "}Total <span className="text-[#00F0FF] font-bold">{formatMs(p1Finish.total)}</span>
                </div>
              </>
            )}
            {mode === "coop" && (
              <>
                <div className="font-heading font-black text-3xl uppercase tracking-tighter">
                  {coopWinner === 1 ? "P1 wins ⚡" : "P2 wins 🔥"}
                </div>
                <div className="mono text-sm text-white/70 mt-2">
                  P1 total <span className="text-[#00F0FF] font-bold">{formatMs(p1Finish.total)}</span>{" · "}
                  P2 total <span className="text-[#FF003C] font-bold">{formatMs(p2Finish.total)}</span>
                </div>
              </>
            )}
            <button onClick={() => start(mode)} data-testid="embed-racer-restart"
              className="mt-4 inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-black mono text-xs uppercase tracking-widest px-6 py-3 rounded-full">
              <RotateCcw className="w-4 h-4" /> Race again
            </button>
            <div className="mt-4 flex justify-center">
              <AdReward game="jixxeo-racer" compact />
            </div>
          </div>
        )}

        <div className="mono text-[9px] uppercase tracking-widest text-white/40 mt-4 text-center flex items-center gap-2 justify-center">
          <Zap className="w-3 h-3" /> three.js webgl · single- or split-screen · touch controls on mobile
        </div>
      </div>
    </div>
  );
}

function ModeSelect({ onStart, p1Best, isTouch, board, user }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
      <div className="glass rounded-2xl p-6 md:p-10 border border-white/10">
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#FFE300] mb-3">// choose your race</div>
        <div className="font-heading font-black text-4xl md:text-5xl uppercase tracking-tighter leading-none">Hit the throttle</div>
        <div className="text-white/60 text-sm mt-3 max-w-lg">
          Chase the global lap record solo, or drag a friend into a split-screen showdown. WASD or arrows, keyboard or {isTouch ? <span className="text-[#00F0FF]">thumb-controls</span> : "keyboard"}.
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          <button
            data-testid="embed-racer-start"
            onClick={() => onStart("solo")}
            className="text-left rounded-2xl border-2 border-[#00F0FF]/40 bg-[#00F0FF]/10 hover:bg-[#00F0FF]/20 p-5 transition-colors"
          >
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-[#00F0FF]" />
              <div className="mono text-[10px] uppercase tracking-widest text-[#00F0FF]">solo · leaderboard</div>
            </div>
            <div className="font-heading font-black text-2xl uppercase tracking-tighter mt-2">Time Trial</div>
            <div className="mono text-[11px] text-white/60 mt-1">
              3-lap sprint. Your best lap submits to the global top-20.
            </div>
            <div className="mt-3 mono text-[10px] uppercase tracking-widest text-white/50">
              Best: <span className="text-[#39FF14] font-bold">{p1Best ? formatMs(p1Best) : "—"}</span>
            </div>
          </button>

          <button
            data-testid="embed-racer-start-coop"
            onClick={() => onStart("coop")}
            className="text-left rounded-2xl border-2 border-[#FF003C]/40 bg-[#FF003C]/10 hover:bg-[#FF003C]/20 p-5 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-[#FF003C]" />
              <div className="mono text-[10px] uppercase tracking-widest text-[#FF003C]">2p · same device</div>
            </div>
            <div className="font-heading font-black text-2xl uppercase tracking-tighter mt-2">Split-Screen</div>
            <div className="mono text-[11px] text-white/60 mt-1">
              P1: <span className="text-white">WASD</span> · P2: <span className="text-white">Arrow keys</span>. Lowest total time wins.
            </div>
            <div className="mt-3 mono text-[10px] uppercase tracking-widest text-white/50">
              Best-of-3 laps · hot-lap your friend
            </div>
          </button>
        </div>

        <div className="mt-6 mono text-[10px] uppercase tracking-widest text-white/40">
          Live online lobby (join by 4-char code) coming next.
        </div>
      </div>
      <Leaderboard board={board} user={user} />
    </div>
  );
}

function Leaderboard({ board, user }) {
  return (
    <div data-testid="racer-leaderboard" className="glass rounded-2xl p-4 border border-white/10 self-start">
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-4 h-4 text-[#FFE300]" />
        <div className="font-heading font-black text-lg uppercase tracking-tighter">Global Top 20</div>
      </div>
      {board.length === 0 ? (
        <div className="mono text-[10px] uppercase tracking-widest text-white/40">No lap times yet — set the record.</div>
      ) : (
        <ol className="space-y-1">
          {board.map((row, i) => (
            <li key={row.user_id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border ${user && row.user_id === user.id ? "border-[#00F0FF]/50 bg-[#00F0FF]/[0.06]" : "border-transparent"}`}>
              <span className={`mono text-[10px] font-bold w-6 text-right ${i === 0 ? "text-[#FFE300]" : i < 3 ? "text-[#00F0FF]" : "text-white/40"}`}>#{i + 1}</span>
              <span className="w-6 h-6 rounded-full flex items-center justify-center font-heading font-black text-[10px]"
                style={{ background: `${row.avatar_color || "#00F0FF"}22`, color: row.avatar_color || "#00F0FF", border: `1px solid ${row.avatar_color || "#00F0FF"}55` }}>
                {(row.user_name || "?")[0].toUpperCase()}
              </span>
              <span className="text-sm truncate flex-1">{row.user_name || "anon"}</span>
              <span className="mono text-xs text-[#39FF14] tabular-nums">{formatMs(row.lap_time_ms)}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function formatMs(ms) {
  if (!ms || ms === Infinity) return "—";
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  const cs = Math.floor((ms % 1000) / 10);
  const sPad = m > 0 ? 2 : 1;
  const sStr = String(s).padStart(sPad, "0");
  const csStr = String(cs).padStart(2, "0");
  const mStr = m > 0 ? m + ":" : "";
  return mStr + sStr + "." + csStr;
}
